// src/services/notificationService.js
import { db } from './firebase';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
  getDocs,
  limit,
  writeBatch,
  Timestamp
} from 'firebase/firestore';

class NotificationService {
  constructor() {
    this.listeners = [];
    this.callbacks = [];
    // Cache del doc configuracion/notificaciones (5 min) para evitar lecturas repetidas
    this._configCache = null;
    this._configCacheTime = 0;
  }

  // Lee configuracion/notificaciones con cache de 5 min en memoria
  async _obtenerConfigNotificaciones() {
    const ahora = Date.now();
    if (this._configCache && (ahora - this._configCacheTime) < 5 * 60 * 1000) {
      return this._configCache;
    }
    try {
      const snap = await getDoc(doc(db, 'configuracion', 'notificaciones'));
      this._configCache = snap.exists() ? snap.data() : {};
      this._configCacheTime = ahora;
      return this._configCache;
    } catch (e) {
      console.error('Error leyendo configuracion/notificaciones:', e);
      return {};
    }
  }

  // Limpia el cache (llamar cuando se guarda nueva config para que la próxima lectura traiga lo último)
  invalidarCacheConfig() {
    this._configCache = null;
    this._configCacheTime = 0;
  }

  /**
   * Devuelve los emails configurados para un tipo de notificación.
   * Si el tipo no está configurado o la config está vacía/inválida, devuelve fallbackEmails.
   *
   * @param {string} tipoConfig - 'cierres' | 'autorizacion_pago' | 'solicitud_pedido_especial'
   * @param {Array} usuarios - lista completa de usuarios activos (de useCatalogos), cada uno con {id, email, rol, activo}
   * @param {Array<string>} fallbackEmails - emails a usar si no hay config (comportamiento actual)
   * @returns {Promise<Array<string>>}
   */
  async obtenerDestinatariosConfigurados(tipoConfig, usuarios, fallbackEmails) {
    const config = await this._obtenerConfigNotificaciones();
    const cfg = config?.[tipoConfig];

    // Si no hay config válida → fallback
    if (!cfg || !cfg.modo) return fallbackEmails;

    const usuariosActivos = (usuarios || []).filter(u => u && u.email && u.activo !== false);

    if (cfg.modo === 'todos') {
      return usuariosActivos.map(u => u.email);
    }
    if (cfg.modo === 'rol') {
      const roles = (cfg.rolesSeleccionados || []).map(r => String(r).toLowerCase());
      if (roles.length === 0) return fallbackEmails;
      return usuariosActivos
        .filter(u => roles.includes(String(u.rol || '').toLowerCase()))
        .map(u => u.email);
    }
    if (cfg.modo === 'individual') {
      const ids = cfg.usuariosSeleccionados || [];
      if (ids.length === 0) return fallbackEmails;
      return usuariosActivos
        .filter(u => ids.includes(u.id))
        .map(u => u.email);
    }

    return fallbackEmails;
  }

  // Crear una notificación
  async crearNotificacion(datos) {
    try {
      const notificacion = {
        titulo: datos.titulo,
        mensaje: datos.mensaje,
        tipo: datos.tipo || 'info', // info, warning, error, success
        prioridad: datos.prioridad || 'normal', // baja, normal, alta, urgente
        usuarioDestino: datos.usuarioDestino, // email del usuario
        usuarioOrigen: datos.usuarioOrigen || 'Sistema',
        moduloOrigen: datos.moduloOrigen || '',
        referenciaId: datos.referenciaId || '', // ID del documento relacionado
        referenciaTipo: datos.referenciaTipo || '', // pedido, solicitud, etc
        leida: false,
        fechaCreacion: serverTimestamp(),
        fechaLectura: null,
        datos: datos.datosAdicionales || {} // Datos adicionales para la notificación
      };

      const docRef = await addDoc(collection(db, 'notificaciones'), notificacion);
      return docRef.id;
    } catch (error) {
      console.error('Error creando notificación:', error);
      throw error;
    }
  }

  // Escuchar notificaciones de un usuario
  escucharNotificaciones(emailUsuario, callback) {
    const q = query(
      collection(db, 'notificaciones'),
      where('usuarioDestino', '==', emailUsuario),
      where('leida', '==', false),
      orderBy('fechaCreacion', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notificaciones = [];
      snapshot.forEach(doc => {
        notificaciones.push({ id: doc.id, ...doc.data() });
      });
      callback(notificaciones);
    });

    this.listeners.push(unsubscribe);
    return unsubscribe;
  }

  // Marcar notificación como leída
  async marcarComoLeida(notificacionId) {
    try {
      await updateDoc(doc(db, 'notificaciones', notificacionId), {
        leida: true,
        fechaLectura: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  }

  // Marcar todas como leídas
  async marcarTodasComoLeidas(emailUsuario) {
    try {
      const q = query(
        collection(db, 'notificaciones'),
        where('usuarioDestino', '==', emailUsuario),
        where('leida', '==', false)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.forEach(doc => {
        batch.update(doc.ref, {
          leida: true,
          fechaLectura: serverTimestamp()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
    }
  }

  // Obtener historial de notificaciones.
  // filtros opcionales: { tipo, leida, modulo, fechaInicio (Date), fechaFin (Date) }
  // Cuando se pasa fechaInicio/fechaFin, el filtro se aplica directamente en Firestore
  // para evitar traer documentos fuera del rango (ahorra lecturas).
  async obtenerHistorial(emailUsuario, filtros = {}) {
    try {
      const condiciones = [
        where('usuarioDestino', '==', emailUsuario)
      ];

      if (filtros.fechaInicio instanceof Date) {
        condiciones.push(where('fechaCreacion', '>=', Timestamp.fromDate(filtros.fechaInicio)));
      }
      if (filtros.fechaFin instanceof Date) {
        condiciones.push(where('fechaCreacion', '<=', Timestamp.fromDate(filtros.fechaFin)));
      }

      const q = query(
        collection(db, 'notificaciones'),
        ...condiciones,
        orderBy('fechaCreacion', 'desc')
      );

      const snapshot = await getDocs(q);
      const notificaciones = [];

      snapshot.forEach(doc => {
        const data = doc.data();

        // Filtros que se aplican en memoria (no requieren índice)
        if (filtros.tipo && data.tipo !== filtros.tipo) return;
        if (filtros.leida !== undefined && data.leida !== filtros.leida) return;
        if (filtros.modulo && data.moduloOrigen !== filtros.modulo) return;

        notificaciones.push({ id: doc.id, ...data });
      });

      return notificaciones;
    } catch (error) {
      console.error('Error obteniendo historial:', error);
      return [];
    }
  }

  // Obtener notificaciones ENVIADAS por un usuario, opcionalmente filtradas por módulo y rango de fechas.
  // Cada doc representa un destinatario; el agrupamiento por envío se hace en el componente
  // (usando datosAdicionales.envioId o referenciaId).
  // filtros: { modulo, fechaInicio (Date), fechaFin (Date) }
  async obtenerHistorialEnviado(emailRemitente, nombreRemitente, filtros = {}) {
    try {
      // Buscamos por email Y por nombre porque históricamente se guardó usuarioOrigen como nombre.
      // Hacemos dos queries y unimos los resultados.
      const condicionesBase = [];
      if (filtros.modulo) {
        condicionesBase.push(where('moduloOrigen', '==', filtros.modulo));
      }
      if (filtros.fechaInicio instanceof Date) {
        condicionesBase.push(where('fechaCreacion', '>=', Timestamp.fromDate(filtros.fechaInicio)));
      }
      if (filtros.fechaFin instanceof Date) {
        condicionesBase.push(where('fechaCreacion', '<=', Timestamp.fromDate(filtros.fechaFin)));
      }

      // Match por nombre (usuarioOrigen guarda el nombre del remitente).
      if (!nombreRemitente) return [];
      const q = query(
        collection(db, 'notificaciones'),
        where('usuarioOrigen', '==', nombreRemitente),
        ...condicionesBase,
        orderBy('fechaCreacion', 'desc')
      );
      const snap = await getDocs(q);
      const docs = [];
      snap.forEach(d => docs.push({ id: d.id, ...d.data() }));
      return docs;
    } catch (error) {
      console.error('Error obteniendo historial enviado:', error);
      return [];
    }
  }

  // Limpiar listeners
  limpiarListeners() {
    this.listeners.forEach(unsubscribe => unsubscribe());
    this.listeners = [];
  }

  // Reproducir sonido de notificación
  //
  // 🔴 Es un ARCHIVO PROPIO del sistema (`public/sonidos/notificacion.wav`), no
  // tonos generados en vivo. Con tonos programados (Web Audio) el aviso sonaba
  // bien la primera vez y después quedaba "lejano": el reloj del contexto de
  // audio se desfasa cuando el navegador lo suspende y reactiva, y la subida de
  // volumen quedaba agendada en el pasado. Un archivo se reproduce siempre
  // igual, sin depender de relojes ni del estado del audio del equipo.
  // El .wav trae 0,25 s de SILENCIO al inicio: es el pedazo que Windows se come
  // al encender el dispositivo, así la campana nunca pierde su ataque.
  // Para cambiar el sonido: reemplazar ese archivo (mismo nombre) — nada de código.
  reproducirSonido() {
    try {
      // 🔴 UNA INSTANCIA NUEVA POR AVISO — no reutilizar una sola y rebobinarla:
      // si un aviso llega mientras el anterior todavía suena (el .wav dura 1,15 s),
      // rebobinarlo lo corta por la mitad y se escucha "raro". Con instancias
      // independientes, dos avisos seguidos suenan cada uno completo.
      // El archivo lo cachea el navegador, así que no se vuelve a descargar.
      const audio = new Audio(`${process.env.PUBLIC_URL || ''}/sonidos/notificacion.wav`);
      audio.volume = 1;
      audio.play().catch(() => this.reproducirSonidoRespaldo());
    } catch (error) {
      this.reproducirSonidoRespaldo();
    }
  }

  // Respaldo: el beep corto original (por si el navegador no tiene Web Audio).
  reproducirSonidoRespaldo() {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJnfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N6QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBziS1/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBw==');
      audio.volume = 0.3;
      audio.play().catch(e => console.log('No se pudo reproducir sonido'));
    } catch (error) {
      console.log('Error con el sonido:', error);
    }
  }
}

export default new NotificationService();